import {
  CUSTOMER_PASSWORD_POLICY_LIMITS,
  DEFAULT_CUSTOMER_PASSWORD_POLICY,
  type CustomerPasswordPolicy,
} from '@/types/organization';
import { clampMinutes } from '@/lib/auth/authChallengeSettings';

/**
 * Per-org customer password policy helper (#652).
 *
 * Reads `settings.customer_password_policy` from the JSONB
 * `organizations.settings` blob, clamping lengths to safe bounds. Runs on
 * both the server and in the browser so the live checklist and the server
 * check always agree.
 *
 * The character-type rules mirror Supabase Auth (GoTrue)
 * `lower_upper_letters_digits_symbols`, which stays on project-wide, so they
 * are always required. GoTrue measures length in bytes, so the maximum is
 * checked against the UTF-8 byte length (72-byte bcrypt cap).
 */

/** The symbol set GoTrue accepts for its "symbols" requirement. */
export const CUSTOMER_PASSWORD_SPECIAL_CHARACTERS = '!@#$%^&*()_+-=[]{};\'\\:"|<>?,./`~';

export type CustomerPasswordRuleId =
  | 'min_length'
  | 'max_length'
  | 'uppercase'
  | 'lowercase'
  | 'number'
  | 'special';

export interface CustomerPasswordRuleResult {
  id: CustomerPasswordRuleId;
  label: string;
  met: boolean;
}

const MIN_LIMITS = CUSTOMER_PASSWORD_POLICY_LIMITS.min_length;
const MAX_LIMITS = CUSTOMER_PASSWORD_POLICY_LIMITS.max_length;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** Normalise a raw `customer_password_policy` value into a clamped, complete policy. */
export function normaliseCustomerPasswordPolicy(raw: unknown): CustomerPasswordPolicy {
  if (!isPlainObject(raw)) return { ...DEFAULT_CUSTOMER_PASSWORD_POLICY };
  const min_length = clampMinutes(
    raw.min_length,
    MIN_LIMITS.min,
    MIN_LIMITS.max,
    DEFAULT_CUSTOMER_PASSWORD_POLICY.min_length,
  );
  const max_length = clampMinutes(
    raw.max_length,
    Math.max(MAX_LIMITS.min, min_length),
    MAX_LIMITS.max,
    DEFAULT_CUSTOMER_PASSWORD_POLICY.max_length,
  );
  return { ...DEFAULT_CUSTOMER_PASSWORD_POLICY, min_length, max_length };
}

/** Read the customer password policy from an `organizations.settings` blob. */
export function getCustomerPasswordPolicy(settings: unknown): CustomerPasswordPolicy {
  if (!isPlainObject(settings)) return { ...DEFAULT_CUSTOMER_PASSWORD_POLICY };
  return normaliseCustomerPasswordPolicy(settings.customer_password_policy);
}

/**
 * Strict validation of an admin-submitted policy. Returns `null` when valid,
 * otherwise a UK English message. Character-type rules may be omitted but
 * cannot be switched off.
 */
export function validateCustomerPasswordPolicyInput(raw: unknown): string | null {
  if (!isPlainObject(raw)) return 'Password policy must be an object';
  const { min_length, max_length } = raw;
  if (
    typeof min_length !== 'number' ||
    !Number.isInteger(min_length) ||
    min_length < MIN_LIMITS.min ||
    min_length > MIN_LIMITS.max
  ) {
    return `Minimum length must be a whole number between ${MIN_LIMITS.min} and ${MIN_LIMITS.max}`;
  }
  if (
    typeof max_length !== 'number' ||
    !Number.isInteger(max_length) ||
    max_length < MAX_LIMITS.min ||
    max_length > MAX_LIMITS.max
  ) {
    return `Maximum length must be a whole number between ${MAX_LIMITS.min} and ${MAX_LIMITS.max}`;
  }
  if (max_length < min_length) {
    return 'Maximum length cannot be less than the minimum length';
  }
  for (const key of [
    'require_uppercase',
    'require_lowercase',
    'require_number',
    'require_special',
  ] as const) {
    if (key in raw && raw[key] !== true) {
      return 'Uppercase, lowercase, number and special character rules cannot be switched off';
    }
  }
  return null;
}

function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

/** Evaluate every rule, for the live checklist. */
export function evaluateCustomerPassword(
  password: string,
  policy: CustomerPasswordPolicy,
): CustomerPasswordRuleResult[] {
  const chars = Array.from(password).length;
  return [
    { id: 'min_length', label: `At least ${policy.min_length} characters`, met: chars >= policy.min_length },
    {
      id: 'max_length',
      label: `No more than ${policy.max_length} characters`,
      met: utf8ByteLength(password) <= policy.max_length,
    },
    { id: 'uppercase', label: 'An uppercase letter (A–Z)', met: /[A-Z]/.test(password) },
    { id: 'lowercase', label: 'A lowercase letter (a–z)', met: /[a-z]/.test(password) },
    { id: 'number', label: 'A number (0–9)', met: /[0-9]/.test(password) },
    {
      id: 'special',
      label: 'A special character, such as ! @ # $ % or &',
      met: Array.from(password).some((c) => CUSTOMER_PASSWORD_SPECIAL_CHARACTERS.includes(c)),
    },
  ];
}

/** Returns the labels of unmet rules; an empty list means the password is valid. */
export function validateCustomerPassword(
  password: unknown,
  policy: CustomerPasswordPolicy,
): string[] {
  const value = typeof password === 'string' ? password : '';
  return evaluateCustomerPassword(value, policy)
    .filter((rule) => !rule.met)
    .map((rule) => rule.label);
}

/** One-line UK English description of the policy for on-screen copy. */
export function describePolicy(policy: CustomerPasswordPolicy): string {
  return `Password must be ${policy.min_length}–${policy.max_length} characters and include an uppercase letter, a lowercase letter, a number and a special character`;
}
